import React, { createContext, useContext, useState, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Tworzymy kontekst dla notyfikacji
const NotificationContext = createContext();

// Hook do używania notyfikacji w komponentach
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

// Komponent dostawcy notyfikacji
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Funkcja do wyświetlania notyfikacji
  const showNotification = useCallback(
    (message, severity = "info", duration = 6000) => {
      const id = Date.now() + Math.random(); // Unique ID for each notification

      setNotifications((prev) => [
        ...prev,
        {
          id,
          message,
          severity,
          duration,
          open: true,
        },
      ]);

      // Auto-remove notification after duration
      setTimeout(() => {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === id ? { ...notif, open: false } : notif,
          ),
        );

        // Remove from array after exit animation (300ms)
        setTimeout(() => {
          setNotifications((prev) => prev.filter((notif) => notif.id !== id));
        }, 300);
      }, duration);
    },
    [],
  );

  // Funkcja do zamykania notyfikacji
  const handleClose = (id) => (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, open: false } : notif,
      ),
    );

    // Remove from array after exit animation
    setTimeout(() => {
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    }, 300);
  };

  return (
    <NotificationContext.Provider
      value={{ showNotification, addNotification: showNotification }}
    >
      {children}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={notification.open}
          autoHideDuration={null} // We handle auto-hide manually for better control
          onClose={handleClose(notification.id)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{
            bottom: {
              xs: 16 + index * 80,
              sm: 24 + index * 80,
              md: 32 + index * 80,
            },
            left: { xs: 16, sm: 24, md: 32 },
            transition: "bottom 0.3s ease-in-out",
          }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleClose(notification.id)}
            severity={notification.severity}
            sx={{
              width: "100%",
              minWidth: "300px",
              maxWidth: "400px",
            }}
          >
            {notification.message}
          </MuiAlert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
